import React, { useState, useEffect } from "react";
import { Slider, Box } from "@material-ui/core";
import StreamStyles from "../styles/StreamStyles";
import StreamMarkerPreview from "./StreamMarkerPreview";
import StreamMarkerCreate from "./StreamMarkerCreate";
import { isDefined } from "../../../util/object-util";
import StreamMarkerControls from "./StreamMarkerControls";

const StreamMarker = ({
	videoPlayer,
	snapshot,
	markers,
	onSave,
	onDelete,
	footballPlays,
	gameConfig
}) => {
	const classes = StreamStyles();

	const [createMarker, setCreateMarker] = useState(false);
	const [freezeMarker, setFreezeMarker] = useState(false);
	const [selectedId, setSelectedId] = useState(null);

	const sortedMarkers = markers.sort((a, b) => a.seconds - b.seconds);

	const marker = markers.find((m) => m.id === selectedId);

	useEffect(() => {
		const marks = document.getElementsByClassName("MuiSlider-mark");
		sortedMarkers.forEach((_, i) => {
			marks[i]?.classList.remove(classes.sliderMarkActive);
		});
		const selectedIndex = sortedMarkers.findIndex((h) => h.id === selectedId);
		if (selectedIndex || selectedIndex === 0) {
			marks[selectedIndex]?.classList.add(classes.sliderMarkActive);
		}
	}, [sortedMarkers, classes.sliderMarkActive, selectedId]);

	const handleSaveMarker = (values) => {
		setCreateMarker(false);
		onSave(values);
	};

	const handleDeleteMarker = (id) => {
		setSelectedId(null);
		setFreezeMarker(false);
		onDelete(id);
	};

	const handleJumpToMarker = (id) => {
		const jumpMarker = sortedMarkers.find((m) => m.id === id);
		videoPlayer.currentTime(jumpMarker.seconds);
		videoPlayer.play();
		setFreezeMarker(false);
		setSelectedId(null);
	};

	const getMappedMarks = (elem) => {
		return [...elem.getElementsByClassName("MuiSlider-mark")].map((e) => ({
			x: e.getBoundingClientRect().left - elem.getBoundingClientRect().left,
			index: Number(e.getAttribute("data-index"))
		}));
	};

	const handleMouseMove = (e) => {
		const mappedMarks = getMappedMarks(e.currentTarget);
		const mouseX = e.clientX - e.currentTarget.getBoundingClientRect().left;

		let foundId = null;

		mappedMarks.forEach((m) => {
			const dist = Math.abs(mouseX - m.x);
			if (dist <= 20) {
				foundId = sortedMarkers[m.index]?.id;
			}
		});

		if (!freezeMarker) {
			setSelectedId(foundId);
		}
	};

	return (
		<Box className={classes.streamMkrContainer}>
			<Slider
				disabled={!markers.length || !snapshot?.duration || createMarker}
				min={0}
				max={Math.max(snapshot?.duration || 0, ...markers.map((m) => m.seconds))}
				defaultValue={null}
				step={null}
				value={sortedMarkers.map(({ seconds }) => seconds)}
				onClick={(e) => {
					const selectedIndex = e.target?.getAttribute("data-index");

					const markerId = isDefined(selectedIndex)
						? sortedMarkers[selectedIndex]?.id
						: null;

					if (freezeMarker && markerId === selectedId) {
						setFreezeMarker(false);
					} else if (markerId) {
						setFreezeMarker(true);
						setSelectedId(markerId);
					}
				}}
				valueLabelDisplay="off"
				marks={sortedMarkers.map(({ seconds }) => ({
					value: seconds
				}))}
				track={false}
				onMouseMove={handleMouseMove}
				onMouseOut={() => {
					if (!freezeMarker) setSelectedId(null);
				}}
			/>
			<StreamMarkerControls
				disabled={!snapshot?.duration}
				sortedMarkers={sortedMarkers}
				marker={marker}
				createMarker={createMarker}
				selectedId={selectedId}
				freezeMarker={freezeMarker}
				handleSaveMarker={handleSaveMarker}
				setCreateMarker={setCreateMarker}
				setFreezeMarker={setFreezeMarker}
				setSelectedId={setSelectedId}
			/>
			{marker?.id && (
				<StreamMarkerPreview
					marker={marker}
					handleDeleteMarker={handleDeleteMarker}
					handleJumpToMarker={handleJumpToMarker}
					footballPlays={footballPlays}
					gameConfig={gameConfig}
				/>
			)}
			{createMarker && (
				<StreamMarkerCreate
					handleSave={handleSaveMarker}
					handleCancel={() => setCreateMarker(false)}
					marker={marker}
					markers={markers}
					snapshot={snapshot}
					currentTime={videoPlayer.currentTime()}
					footballPlays={footballPlays}
					gameConfig={gameConfig}
				/>
			)}
		</Box>
	);
};

export default StreamMarker;
